import React, { useState, useEffect } from "react";

function MonthlyPaymentEstimator({
  basePrice,
  selectedProgram,
  selectedProgramTotal,
  perComponentRate,
  chosenComponents = [],
}) {
  const [financingTerm, setFinancingTerm] = useState(60);
  const [apr, setApr] = useState(8);
  const [monthlyPayment, setMonthlyPayment] = useState(0);
  const [currentPrice, setCurrentPrice] = useState(0);
  const [editingTerm, setEditingTerm] = useState(false);
  const [editingApr, setEditingApr] = useState(false);

  // Determine price source and calculate price
  useEffect(() => {
    // Calculate the total price for per-component option
    const calculatePerComponentPrice = () => {
      if (!perComponentRate) return 0;

      // Start with base price
      let total = perComponentRate.retail_price || perComponentRate.cost || 0;

      // Add price for each chosen component
      if (perComponentRate.products && chosenComponents.length > 0) {
        perComponentRate.products.forEach((product) => {
          if (chosenComponents.includes(product.id)) {
            total += product.retail_price || product.cost || 0;
          }
        });
      }

      return total;
    };

    let price = 0;
    let source = "base price";

    // Check if selected program is the per-component option
    const isPerComponentSelected =
      selectedProgram &&
      perComponentRate &&
      selectedProgram.id === perComponentRate.id;

    if (isPerComponentSelected) {
      // For per-component option, calculate based on selected components
      price = calculatePerComponentPrice();
      source = "per-component selections";
    } else if (selectedProgram) {
      // Normal selected program - prefer the live total (includes any
      // toggled optional products, e.g. Tech) over the static rate price
      price =
        selectedProgramTotal ??
        selectedProgram.retail_price ??
        selectedProgram.cost ??
        0;
      source = "selected program";
    } else {
      // Default to base price
      price = basePrice?.retail_price || basePrice?.cost || 0;
      source = "base price";
    }

    console.log("Current price for calculation:", price, "from", source);
    setCurrentPrice(price);
  }, [
    selectedProgram,
    selectedProgramTotal,
    basePrice,
    perComponentRate,
    chosenComponents,
  ]);

  // Calculate monthly payment based on currentPrice
  useEffect(() => {
    const calculatePayment = () => {
      if (currentPrice === 0 || financingTerm === 0) {
        setMonthlyPayment(0);
        return;
      }

      // Convert annual rate to monthly rate
      const monthlyInterestRate = apr / 100 / 12;

      if (monthlyInterestRate === 0) {
        // Simple division if no interest
        setMonthlyPayment(currentPrice / financingTerm);
        return;
      }

      // Standard loan payment formula: P * r * (1+r)^n / ((1+r)^n - 1)
      // where P = principal, r = monthly interest rate, n = number of payments
      const numerator =
        monthlyInterestRate * Math.pow(1 + monthlyInterestRate, financingTerm);
      const denominator = Math.pow(1 + monthlyInterestRate, financingTerm) - 1;
      const payment = currentPrice * (numerator / denominator);

      setMonthlyPayment(payment);
    };

    calculatePayment();
  }, [financingTerm, apr, currentPrice]);

  const handleFinancingTermChange = (e) => {
    setFinancingTerm(parseInt(e.target.value));
  };

  const handleFinancingTermInputChange = (e) => {
    const val = parseInt(e.target.value);
    if (!isNaN(val)) setFinancingTerm(Math.min(96, Math.max(12, val)));
  };

  const handleFinancingTermBlur = () => {
    setEditingTerm(false);
  };

  const handleFinancingTermKeyDown = (e) => {
    if (e.key === "Enter") setEditingTerm(false);
  };

  const handleAprChange = (e) => {
    setApr(parseFloat(e.target.value));
  };

  const handleAprInputChange = (e) => {
    const val = parseFloat(e.target.value);
    if (!isNaN(val)) setApr(Math.min(23.99, Math.max(0, parseFloat(val.toFixed(2)))));
  };

  const handleAprBlur = () => {
    setEditingApr(false);
  };

  const handleAprKeyDown = (e) => {
    if (e.key === "Enter") setEditingApr(false);
  };

  // Format currency for display
  const formatCurrency = (amount) => {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(amount);
  };

  return (
    <div className="monthly-payment-estimator mt-2 p-3 border rounded">
      <h5 className="mb-3">Monthly Payment Estimator</h5>

      <div className="row mb-3">
        <div className="col-md-6">
          <label htmlFor="financingTerm" className="form-label">
            Financing Term:{" "}
            {editingTerm ? (
              <input
                type="number"
                className="form-control form-control-sm d-inline-block w-auto"
                min="12"
                max="96"
                step="1"
                value={financingTerm}
                onChange={handleFinancingTermInputChange}
                onBlur={handleFinancingTermBlur}
                onKeyDown={handleFinancingTermKeyDown}
                autoFocus
              />
            ) : (
              <span
                className="text-decoration-underline"
                style={{ cursor: "pointer" }}
                onClick={(e) => { e.preventDefault(); setEditingTerm(true); }}
                title="Click to edit"
              >
                {financingTerm}
              </span>
            )}{" "}
            months
          </label>
          <input
            type="range"
            className="form-range"
            id="financingTerm"
            min="12"
            max="96"
            step="6"
            value={financingTerm}
            onChange={handleFinancingTermChange}
          />
        </div>

        <div className="col-md-6">
          <label htmlFor="apr" className="form-label">
            APR:{" "}
            {editingApr ? (
              <input
                type="number"
                className="form-control form-control-sm d-inline-block w-auto"
                min="0"
                max="23.99"
                step="0.01"
                value={apr}
                onChange={handleAprInputChange}
                onBlur={handleAprBlur}
                onKeyDown={handleAprKeyDown}
                autoFocus
              />
            ) : (
              <span
                className="text-decoration-underline"
                style={{ cursor: "pointer" }}
                onClick={(e) => { e.preventDefault(); setEditingApr(true); }}
                title="Click to edit"
              >
                {apr.toFixed(2)}
              </span>
            )}%
          </label>
          <input
            type="range"
            className="form-range"
            id="apr"
            min="0"
            max="23.99"
            step="0.25"
            value={apr}
            onChange={handleAprChange}
          />
        </div>
      </div>

      <div className="row">
        <div className="col-12 text-center">
          <div className="h4">
            Estimated Monthly Payment: {formatCurrency(monthlyPayment)}
          </div>
          <div className="small text-muted">
            Based on {currentPrice > 0 ? formatCurrency(currentPrice) : "$0"}
            {selectedProgram &&
            perComponentRate &&
            selectedProgram.id === perComponentRate.id
              ? " (per-component selections)"
              : selectedProgram
                ? " (selected program)"
                : " (full Affordable Option price)"}
          </div>
        </div>
      </div>
    </div>
  );
}

export default MonthlyPaymentEstimator;
